import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPostJson } from './client'

/**
 * Settings > Securitee > XDR > SIEM groups - conexiunea Wazuh a unui grup
 * SIEM (API: `App\Securitee\Xdr\WazuhConnection`); grupul 1 e Shared, cel de
 * dinainte de grupuri. 403 cui nu poate edita grupul.
 *
 *  - `GET  /securitee/xdr-settings/wazuh`       randul, fara parola (`password_set`)
 *  - `POST /securitee/xdr-settings/wazuh`       salveaza; 422 `{error: 'field', field, message}`
 *  - `POST /securitee/xdr-settings/wazuh/test`  incearca valorile din formular, fara sa salveze
 *
 * `field` e `syslog.host`, `syslog.port`, `indexer.url`, `indexer.user`,
 * `indexer.password`...
 */

/** `portal` = pus aici; `env` = gol aici, ph4 ia `.env`-ul LUI (portalul nu-l vede). */
export type WazuhSource = 'portal' | 'env' | 'none'

export interface WazuhConnectionPage {
  syslog: { host: string; port: number | null; tls: boolean }
  indexer: { url: string; user: string; password_set: boolean; insecure: boolean }
  source?: { syslog: WazuhSource; indexer: WazuhSource }
  flow?: { last_forward_at: string | null; last_alert_read_at: string | null }
  /** banda "XDR flow stopped": minutele firmei-platforma (null = implicit) */
  flow_alert?: { minutes: number | null; default: number; min: number; max: number }
  /** webhook-ul de alerte din Wazuh; `new_token` / `url` numai in raspunsul la generare */
  webhook?: { token_set: boolean; ips: string; base_url: string; new_token?: string; url?: string }
  /** grupul SIEM al conexiunii (1 = Shared) */
  group?: number
  /** false = portalul n-are DEFUSE_CRYPT_PASS si nu poate salva o parola */
  crypto_ready: boolean
  updated_at: string | null
  updated_by_name: string
}

export interface WazuhConnectionForm {
  syslog: { host: string; port: number | string | null; tls: boolean }
  /** `password` gol = ramane cea salvata; `clear_password` = se sterge */
  indexer: { url: string; user: string; password: string; clear_password: boolean; insecure: boolean }
  flow_alert_minutes: number | string | null
}

export type WazuhTestTarget = 'syslog' | 'indexer'

export interface WazuhTestResult {
  ok: boolean
  message: string
  ms: number
  status?: number | null
  cluster_name?: string
  version?: string
}

export const WAZUH_CONNECTION_KEY = ['securitee', 'xdr-settings', 'wazuh'] as const

/** Cheia conexiunii unui grup; Shared ramane pe cheia de dinainte de grupuri. */
export function wazuhConnectionKey(group = 1) {
  return group === 1 ? WAZUH_CONNECTION_KEY : ([...WAZUH_CONNECTION_KEY, group] as const)
}

export function wazuhConnectionQuery(group = 1) {
  return queryOptions({
    queryKey: wazuhConnectionKey(group),
    queryFn: () => apiGet<WazuhConnectionPage>('/securitee/xdr-settings/wazuh' + (group === 1 ? '' : `?group=${group}`)),
  })
}

export function saveWazuhConnection(body: WazuhConnectionForm, group = 1): Promise<WazuhConnectionPage> {
  return apiPostJson<WazuhConnectionPage>('/securitee/xdr-settings/wazuh', group === 1 ? body : { ...body, group })
}

export function testWazuhConnection(target: WazuhTestTarget, body: WazuhConnectionForm, group = 1): Promise<WazuhTestResult> {
  return apiPostJson<WazuhTestResult>('/securitee/xdr-settings/wazuh/test', group === 1 ? { target, ...body } : { target, ...body, group })
}

export type WazuhWebhookAction = { action: 'generate' } | { action: 'clear' } | { action: 'ips'; ips: string }

export function saveWazuhWebhook(body: WazuhWebhookAction, group = 1): Promise<WazuhConnectionPage> {
  return apiPostJson<WazuhConnectionPage>('/securitee/xdr-settings/wazuh/webhook', group === 1 ? body : { ...body, group })
}
